import React, { useCallback, useEffect, useState } from 'react'
import { getConfig } from '../get-import-config'
import BaseActionButton from '../../Page/BaseActionButton'
import Modal from '../../Modal'
import ImportTeamRosterContent from './ImportTeamRosterContent'

/**
 * Import Roster: renders its own trigger button AND the modal.
 *
 * `orgId` is omitted for a single team and supplied only for the Org screen's
 * multi-team import — the JSDoc makes that optionality visible to TypeScript
 * callers, which bare destructuring would infer as required.
 *
 * @param {Object} props
 * @param {string} props.teamId
 * @param {string} [props.teamName]
 * @param {string} props.teamRosterId
 * @param {() => void} [props.onSuccess]
 * @param {string} [props.orgId] Multi-team import only; omit for one team.
 * @param {boolean} [props.disabled]
 * @param {string} [props.disabledReason] Tooltip shown while disabled.
 */
const ImportTeamRoster = ({
  teamId,
  teamName,
  teamRosterId,
  onSuccess,
  orgId,
  disabled = false,
  disabledReason = '',
  // 'players' (default) or 'teamOfficials': which roster the file fills.
  rosterType = 'players',
  // Lets a page trigger this control from elsewhere (a dropdown item).
  buttonId,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const isTeamOfficials = rosterType === 'teamOfficials'
  const config = getConfig()
  const importTemplate = orgId
    ? isTeamOfficials
      ? config.multiTeamOfficials
      : config.multiTeamRoster
    : isTeamOfficials
    ? config.teamOfficials
    : config.roster
  const isMultiTeamRoster = orgId ? true : false

  // While an import request runs (loading), every close path is a no-op: the
  // request may still be writing, and closing would hide how it ended.
  const handleEsc = useCallback(
    (event) => {
      if (event.key === 'Escape' && !loading) {
        setIsOpen(false)
      }
    },
    [loading]
  )

  useEffect(() => {
    window.addEventListener('keydown', handleEsc)
    return () => {
      window.removeEventListener('keydown', handleEsc)
    }
  }, [handleEsc])

  const onToggle = () => {
    if (loading) {
      return
    }
    setIsOpen(!isOpen)
  }

  const link = (
    <BaseActionButton
      id={buttonId}
      name={orgId ? 'multi-roster' : 'upload'}
      title={disabled ? disabledReason : importTemplate?.label}
      onClick={() => {
        if (disabled) {
          return
        }
        onToggle()
      }}
      disabled={loading || disabled}
      busy={loading}
    />
  )

  return (
    <>
      {link}
      <Modal
        isOpen={isOpen}
        onClose={onToggle}
        shouldCloseOnOverlayClick={false}
      >
        {isOpen && (
          <ImportTeamRosterContent
            setLoading={setLoading}
            loading={loading}
            toggle={onToggle}
            importTemplate={importTemplate}
            teamName={teamName}
            teamRosterId={teamRosterId}
            teamId={teamId}
            onSuccess={onSuccess}
            orgId={orgId}
            isMultiTeamRoster={isMultiTeamRoster}
            rosterType={rosterType}
          />
        )}
      </Modal>
    </>
  )
}

export default ImportTeamRoster
